import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concatMap,
  defer,
  distinctUntilChanged,
  EMPTY,
  finalize,
  map,
  Observable,
  scan,
  Subject,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs'

import { latestMagentoClient$ } from '../../graphql/apollo'
import { cartMutationTarget$ } from './cart-mutation'
import type { ExpressTarget } from './shipping-write'

/** Every writer that sends cart mutations through the queue. */
export type CartWriteLabel =
  | 'checkout-shipping'
  | 'checkout-order'
  | 'global-promotions'
  | 'practitioner-code-reconcile'
  | 'item-quantity-clamp'
  | 'label-fee-removal'
  | 'items-remove'

export type CartWriteOwner = 'checkout' | 'outside'

const cartWriteOwners: Record<CartWriteLabel, CartWriteOwner> = {
  'checkout-shipping': 'checkout',
  'checkout-order': 'checkout',
  'global-promotions': 'outside',
  'practitioner-code-reconcile': 'outside',
  'item-quantity-clamp': 'outside',
  'label-fee-removal': 'outside',
  'items-remove': 'outside',
}

export const cartWriteOwner = (label: CartWriteLabel): CartWriteOwner => cartWriteOwners[label]

/** The same target the express writes use, so `mutateCart$` accepts it. */
export type CartWriteTarget = ExpressTarget

export type CartWriteJob<T> = (target: CartWriteTarget) => Observable<T>

/** Jobs queued or running, by owner. */
export interface CartWriteCounts {
  checkout: number
  outside: number
}

type CartWriteEvent =
  | { _tag: 'Queued'; owner: CartWriteOwner }
  | { _tag: 'Finished'; owner: CartWriteOwner }

const noCartWrites: CartWriteCounts = { checkout: 0, outside: 0 }

export const applyCartWriteEvent = (
  counts: CartWriteCounts,
  { _tag, owner }: CartWriteEvent,
): CartWriteCounts => ({ ...counts, [owner]: counts[owner] + (_tag === 'Queued' ? 1 : -1) })

/** The cart and client a job writes; a read that must not queue resolves it the same way. */
export const cartWriteTarget$ = (): Observable<CartWriteTarget> =>
  combineLatest([latestMagentoClient$, cartMutationTarget$()]).pipe(
    take(1),
    map(([client, { cart, token }]) => ({ client, cart, token })),
  )

export interface CartWriteQueue {
  /**
   * Queue `job` on subscription. It runs once every earlier job has finished, against the target
   * resolved when it starts, and the caller receives its values and error. Once queued it runs to
   * completion even if the caller unsubscribes, and it must complete for the next job to start. A
   * job must not wait on another `enqueue`, because that job is queued behind it.
   */
  enqueue: <T>(label: CartWriteLabel, job: CartWriteJob<T>) => Observable<T>
  /** true while any job is queued or running */
  active$: Observable<boolean>
  /** true while a job other than the checkout's own is queued or running */
  busy$: Observable<boolean>
  dispose: () => void
}

/** `resolveTarget` runs as each job starts; tests pass their own cart and client. */
export const createCartWriteQueue = (
  resolveTarget: () => Observable<CartWriteTarget> = cartWriteTarget$,
): CartWriteQueue => {
  const jobs$ = new Subject<Observable<unknown>>()
  const events$ = new Subject<CartWriteEvent>()
  // held rather than derived per subscriber, so a late subscriber sees jobs queued before it
  const counts$ = new BehaviorSubject<CartWriteCounts>(noCartWrites)
  const subscription = new Subscription()
  // jobs first: teardown runs in this order, and a running job's `Finished` needs the scan alive
  subscription.add(jobs$.pipe(concatMap((job) => job)).subscribe())
  subscription.add(events$.pipe(scan(applyCartWriteEvent, noCartWrites)).subscribe(counts$))

  const enqueue = <T>(label: CartWriteLabel, job: CartWriteJob<T>): Observable<T> =>
    new Observable<T>((subscriber) => {
      const owner = cartWriteOwner(label)
      events$.next({ _tag: 'Queued', owner })
      jobs$.next(
        // resolved at start, not at enqueue, so a job queued before a merge writes the merged cart
        defer(resolveTarget).pipe(
          switchMap(job),
          tap({
            next: (value) => subscriber.next(value),
            error: (error: unknown) => subscriber.error(error),
            complete: () => subscriber.complete(),
          }),
          // keep the queue alive for the next job; the caller already received the error
          catchError(() => EMPTY),
          finalize(() => events$.next({ _tag: 'Finished', owner })),
        ),
      )
    })

  return {
    enqueue,
    active$: counts$.pipe(
      map(({ checkout, outside }) => checkout + outside > 0),
      distinctUntilChanged(),
    ),
    busy$: counts$.pipe(
      map(({ outside }) => outside > 0),
      distinctUntilChanged(),
    ),
    dispose: () => {
      subscription.unsubscribe()
      // queued jobs never start, so their `Finished` never arrives
      counts$.next(noCartWrites)
    },
  }
}

const cartWriteQueue = createCartWriteQueue()

export const enqueueCartWrite = cartWriteQueue.enqueue
export const cartQueueActive$: Observable<boolean> = cartWriteQueue.active$
export const cartWritesBusy$: Observable<boolean> = cartWriteQueue.busy$
